import type {
  AssistantConversation,
  AssistantMessagePage,
  AssistantUpload,
  AssistantUploadSession,
  CreateAssistantUploadRequest,
} from '@xtreed/academy-shared';
import { api } from './api';

function unwrap<T>(data: unknown): T {
  const wrapped = data as { data?: T } | undefined;
  return (wrapped?.data ?? data) as T;
}

export const assistantService = {
  async listConversations(): Promise<AssistantConversation[]> {
    const { data } = await api.get('/assistant/conversations');
    return unwrap<AssistantConversation[]>(data) ?? [];
  },

  async createConversation(title?: string): Promise<AssistantConversation> {
    const { data } = await api.post('/assistant/conversations', { title });
    return unwrap(data);
  },

  async renameConversation(
    id: string,
    title: string,
  ): Promise<AssistantConversation> {
    const { data } = await api.patch(`/assistant/conversations/${id}`, {
      title,
    });
    return unwrap(data);
  },

  async deleteConversation(id: string): Promise<void> {
    await api.delete(`/assistant/conversations/${id}`);
  },

  async messages(
    conversationId: string,
    cursor?: string | null,
  ): Promise<AssistantMessagePage> {
    const { data } = await api.get(
      `/assistant/conversations/${conversationId}/messages`,
      { params: cursor ? { cursor } : undefined },
    );
    return unwrap(data);
  },

  async uploads(conversationId: string): Promise<AssistantUpload[]> {
    const { data } = await api.get(
      `/assistant/conversations/${conversationId}/uploads`,
    );
    return unwrap<AssistantUpload[]>(data) ?? [];
  },

  async createUpload(
    body: CreateAssistantUploadRequest,
  ): Promise<AssistantUploadSession> {
    const { data } = await api.post('/assistant/uploads', body);
    return unwrap(data);
  },

  async completeUpload(id: string): Promise<AssistantUpload> {
    const { data } = await api.post(`/assistant/uploads/${id}/complete`);
    return unwrap(data);
  },

  async failUpload(id: string, reason: string): Promise<AssistantUpload> {
    const { data } = await api.post(`/assistant/uploads/${id}/fail`, {
      reason,
    });
    return unwrap(data);
  },

  async retryUpload(id: string): Promise<AssistantUploadSession> {
    const { data } = await api.post(`/assistant/uploads/${id}/retry`);
    return unwrap(data);
  },
};
